import React, {useState, useRef} from 'react';
import {Button, Input, Space, Table, Tag, Skeleton} from 'antd';
import {SearchOutlined, CarOutlined, ClockCircleOutlined} from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import Title from "../../../components/Title";
import {useSelector} from "react-redux";
import {useGetVehicleQuery} from "../../../slices/rootApiSlices/vehicleApiSlice";
import AddVehicleModal from "./addVehicleModal";
import {Link} from "react-router-dom";
import moment from "moment/moment";

const Index = () => {

    const {user} = useSelector((state) => state.auth);
    const {data, error, isLoading} = useGetVehicleQuery(user?.token);
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef(null);


    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({setSelectedKeys, selectedKeys, confirm, clearFilters, close}) => (
            <div style={{padding: 8}}>
                <Input
                    ref={searchInput}
                    placeholder={`Ara ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{marginBottom: 8, display: 'block'}}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined/>}
                        size="small"
                        style={{width: 90}}
                    >
                        Ara
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{width: 90}}
                    >
                        Sıfırla
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({
                                closeDropdown: false,
                            });
                            setSearchText(selectedKeys[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filtrele
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        Kapat
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined
                style={{
                    color: filtered ? '#1890ff' : undefined,
                }}
            />
        ),
        onFilter: (value, record) => {
            const dataIndexValue = record[dataIndex];
            if (dataIndexValue !== null && dataIndexValue !== undefined) {
                if (dataIndex === 'plateNumber') {
                    return dataIndexValue.replace('n', '').toLowerCase().includes(value.toLowerCase());
                }
                return dataIndexValue.toString().toLowerCase().includes(value.toLowerCase());
            }
            return false;
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{
                        backgroundColor: '#ffc069',
                        padding: 0,
                    }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns = [
        {
            title: 'Kapı No',
            dataIndex: 'operationNumber',
            key: 'operationNumber',
            width: '5%',
            ...getColumnSearchProps('operationNumber'),
            render: (text, row) => (
                <Link style={{justifyContent: "center"}} to={`/settings/vehicles/${row.id}`}>
                    {text ? text : "no name"}
                </Link>
            ),
        },
        {
            title: 'Plaka',
            dataIndex: 'plateNumber',
            key: 'plateNumber',
            width: '7%',
            ...getColumnSearchProps('plateNumber'),
        },
        {
            title: 'Şase No',
            dataIndex: 'chassisNumber',
            key: 'chassisNumber',
            width: '7%',
            ...getColumnSearchProps('chassisNumber'),
        },
        {
            title: 'Device ID',
            dataIndex: 'device_id',
            key: 'device_id',
            width: '10%',
            responsive: ['sm'],
            ...getColumnSearchProps('device_id'),
            sorter: (a, b) => a.device_id.length - b.device_id.length,
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Marka',
            dataIndex: 'brand',
            key: 'brand',
            width: '5%',
            responsive: ['sm'],
            filters: [
                {text: 'Mercedes', value: 'mercedes'},
                {text: 'Otokar', value: 'otokar'},
                {text: 'Tanımlanmadı', value: 'tanımlanmadı'},
            ],
            onFilter: (value, record) => {
                if (value === 'tanımlanmadı') {
                    return !record.brand;
                }
                return record.brand && record.brand.toLowerCase() === value;
            },
            sorter: (a, b) => {
                const brandA = a.brand ? a.brand.toLowerCase() : '';
                const brandB = b.brand ? b.brand.toLowerCase() : '';
                if (brandA < brandB) return -1;
                if (brandA > brandB) return 1;
                return 0;
            },
            sortDirections: ['descend', 'ascend'],
            render: (text) => {
                if (!text) {
                    return <Tag icon={<CarOutlined/>} color="default">Tanımlanmadı</Tag>;
                }
                if (text.toLowerCase() === 'mercedes') {
                    return <Tag icon={<CarOutlined/>} color="blue">Mercedes</Tag>;
                }
                if (text.toLowerCase() === 'otokar') {
                    return <Tag icon={<CarOutlined/>} color="green">Otokar</Tag>;
                }
                return <Tag icon={<CarOutlined/>} color="gold">{text}</Tag>;
            },
        },
        {
            title: 'Oluşturulma Tarihi',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: '7%',
            sorter: (a, b) => moment(a.createdAt).unix() - moment(b.createdAt).unix(),
            sortDirections: ['descend', 'ascend'],
            render: (text) => (
                <Tag icon={<ClockCircleOutlined/>} color="purple">
                    {moment(text).format('DD-MM-YYYY HH:mm:ss')}
                </Tag>
            ),
        },
    ];

    let locale = {
        emptyText: 'Araç bulunamadı.Lütfen araç ekleyin.',
    };

    return (
        <Skeleton loading={isLoading}>
            <div style={{padding: 15}}>
                <Title title="Octoopi Cocpit | Araç Listesi" description="Mertech Arge A.Ş Araç Listesi"/>
                <div style={{justifyContent: "flex-end"}}>
                    <AddVehicleModal/>
                </div>
                <Table
                    locale={locale}
                    rowKey="id"
                    loading={isLoading}
                    bordered={true}
                    size="small"
                    columns={columns}
                    dataSource={data?.data}
                    error={error}
                />
            </div>
        </Skeleton>
    );
};

export default Index;
